import { forwardRef, useCallback, useRef, useState, type MouseEvent } from 'react';
import { Button, type ButtonProps } from './Button';
import s from './Button.module.css';

export interface LoadingButtonProps extends Omit<ButtonProps, 'onClick'> {
  /** `true` mostra o indicador e bloqueia o botão. */
  loading?: boolean;
  /** Texto do estado carregando (o rótulo continua visível ao lado). */
  loadingLabel?: string;
  /**
   * Pode devolver uma promise: enquanto ela não resolve o botão fica bloqueado,
   * mesmo sem `loading` controlado — é o que impede o duplo clique (FR-127).
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<unknown>;
}

/**
 * Botão de escrita (FR-127): mostra carregando e **bloqueia o duplo clique**.
 * Funciona controlado (`loading`) ou não (via a promise do `onClick`).
 *
 * ## Com react-query, use uma destas duas — nunca `mutate()` sozinho
 *
 * `mutate()` devolve `void`: sem promise para esperar, o botão não bloqueia nada
 * e o usuário consegue disparar a ordem duas vezes.
 *
 * ```tsx
 * // 1. Controlado (padrão do projeto): o estado do react-query manda no botão.
 * <LoadingButton loading={salvar.isPending} onClick={() => salvar.mutate(dados)}>
 *
 * // 2. Promise: quando não há hook à mão para ler o `isPending`.
 * <LoadingButton onClick={() => salvar.mutateAsync(dados).catch(() => {})}>
 * ```
 *
 * Na forma 2 o `catch` é obrigatório — `mutateAsync` rejeita, e uma rejeição sem
 * tratamento vira erro não capturado no navegador. O erro em si a tela já mostra
 * pelo `ErrorBanner`, a partir do `code` (FR-123).
 */
export const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  function LoadingButton(
    { loading, loadingLabel = 'Salvando…', onClick, disabled, children, icon, ...rest },
    ref,
  ) {
    const [pending, setPending] = useState(false);
    const inFlight = useRef(false);

    const busy = loading || pending;

    const handleClick = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        if (inFlight.current || loading) {
          e.preventDefault();
          return;
        }
        const result = onClick?.(e);
        if (result && typeof (result as Promise<unknown>).finally === 'function') {
          inFlight.current = true;
          setPending(true);
          void (result as Promise<unknown>).finally(() => {
            inFlight.current = false;
            setPending(false);
          });
        }
      },
      [loading, onClick],
    );

    return (
      <Button
        ref={ref}
        {...rest}
        {...(busy ? {} : icon ? { icon } : {})}
        onClick={handleClick}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
      >
        {busy ? <span className={s.spinner} aria-hidden="true" /> : null}
        {children}
        {busy ? <span className="sr-only">{loadingLabel}</span> : null}
      </Button>
    );
  },
);
